import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../stores/useAppStore'
import { apiEndpoints } from '../config/env'
import { runsheetService } from '../services/runsheetService'
import type { RunsheetClient, RunsheetWorkOrder, KeyUpdateRequest, SignatureRequest } from '../services/runsheetService'
import SignaturePad from '../components/SignaturePad'
import { Select, type SelectOption } from '../components/ui/dropdown-menu'
import {
  KeyIcon,
  MapPinIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  XMarkIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'

import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Checkbox } from '../components/ui/checkbox'
import { Textarea } from '../components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog'

interface ClientKeylistProps {
  client: RunsheetClient
  date: string
  isExpanded: boolean
  onToggle: () => void
  selectedTechnicianId: number
}

const ClientKeylist: React.FC<ClientKeylistProps> = ({ client, date, isExpanded, onToggle, selectedTechnicianId }) => {
  const { addNotification } = useAppStore()
  const queryClient = useQueryClient()
  const [activeSignature, setActiveSignature] = useState<'PU' | 'DO' | null>(null)
  const [signatureData, setSignatureData] = useState<string>('')
  const [localWorkOrders, setLocalWorkOrders] = useState<RunsheetWorkOrder[]>([])
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const [showClientNotesModal, setShowClientNotesModal] = useState(false)
  const [selectedClientNotes, setSelectedClientNotes] = useState<string>('')

  // Get work orders for this client filtered by selected technician
  const { data: workOrders = [], isLoading } = useQuery({
    queryKey: ['clientWorkOrders', client.clientID, date, selectedTechnicianId],
    queryFn: () => runsheetService.getClientWorkOrders(client.clientID, date, selectedTechnicianId),
    staleTime: 0, // Data is immediately stale for aggressive refresh
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  })

  // Update local work orders when server data changes
  useEffect(() => {
    setLocalWorkOrders(workOrders)
  }, [JSON.stringify(workOrders)])

  // Key update mutation
  const keyUpdateMutation = useMutation({
    mutationFn: (keyUpdate: KeyUpdateRequest) => runsheetService.updateKeyInfo(keyUpdate),
    onSuccess: () => {
      addNotification({
        type: 'success',
        message: 'Key information updated successfully',
        timestamp: Date.now()
      })
      queryClient.invalidateQueries({ queryKey: ['clientWorkOrders', client.clientID, date] })
    },
    onError: (error: Error) => {
      // Revert the optimistic update by refreshing from server data
      setLocalWorkOrders(workOrders)
      addNotification({
        type: 'error',
        message: `Failed to update key info: ${error.message}`,
        timestamp: Date.now()
      })
    }
  })

  // Signature mutation
  const signatureMutation = useMutation({
    mutationFn: (signature: SignatureRequest) => runsheetService.saveSignature(signature),
    onSuccess: () => {
      addNotification({
        type: 'success',
        message: 'Signature saved successfully',
        timestamp: Date.now()
      })
      
      // Save signature to localStorage for future use
      if (signatureData) {
        runsheetService.saveSignatureToStorage(signatureData)
      }
      
      // Immediately invalidate and refetch the query to show new signature
      queryClient.invalidateQueries({ queryKey: ['clientWorkOrders', client.clientID, date] })
      
      setActiveSignature(null)
      setSignatureData('')
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        message: `Failed to save signature: ${error.message}`,
        timestamp: Date.now()
      })
    }
  })

  // Debounced key update function
  const debouncedKeyUpdate = useCallback((workOrderID: number, field: string, value: string | boolean) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      // Get the current state of the work order
      setLocalWorkOrders(currentOrders => {
        const currentWorkOrder = currentOrders.find(wo => wo.workOrderID === workOrderID)
        if (!currentWorkOrder) return currentOrders

        const keyUpdate: KeyUpdateRequest = {
          workOrderID: currentWorkOrder.workOrderID,
          propertyID: currentWorkOrder.propertyID,
          puKeyNo: currentWorkOrder.puKeyNo,
          puComment: currentWorkOrder.puComment,
          puAvailable: currentWorkOrder.puAvailable,
          doKeyNo: currentWorkOrder.doKeyNo,
          doComment: currentWorkOrder.doComment,
          doAvailable: currentWorkOrder.doAvailable,
          [field]: value
        }

        keyUpdateMutation.mutate(keyUpdate)
        return currentOrders
      })
    }, 1000)
  }, [keyUpdateMutation])

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  const handleKeyUpdate = (workOrder: RunsheetWorkOrder, field: string, value: string | boolean) => {
    // Optimistically update the local state immediately
    setLocalWorkOrders(prevOrders => 
      prevOrders.map(wo => 
        wo.workOrderID === workOrder.workOrderID 
          ? { ...wo, [field]: value }
          : wo
      )
    )

    if (typeof value === 'boolean') {
      const keyUpdate: KeyUpdateRequest = {
        workOrderID: workOrder.workOrderID,
        propertyID: workOrder.propertyID,
        puKeyNo: workOrder.puKeyNo,
        puComment: workOrder.puComment,
        puAvailable: workOrder.puAvailable,
        doKeyNo: workOrder.doKeyNo,
        doComment: workOrder.doComment,
        doAvailable: workOrder.doAvailable,
        [field]: value
      }

      keyUpdateMutation.mutate(keyUpdate)
    } else {
      debouncedKeyUpdate(workOrder.workOrderID, field, value)
    }
  }

  const handleSignature = (type: 'PU' | 'DO') => {
    setActiveSignature(type)
    // Load saved signature from localStorage
    const savedSignature = runsheetService.getSignatureFromStorage()
    if (savedSignature) {
      setSignatureData(savedSignature)
    }
  }

  const saveSignature = () => {
    if (!signatureData || !activeSignature) {
      addNotification({
        type: 'warning',
        message: 'Please provide a signature',
        timestamp: Date.now()
      })
      return
    }
    
    // Save signature for all work orders of this client
    const workOrderIDs = localWorkOrders.map(wo => wo.workOrderID)
    
    signatureMutation.mutate({
      workOrderIDs,
      clientID: client.clientID,
      keyType: activeSignature,
      signatureBase64: signatureData
    })
  }

  const cancelSignature = () => {
    setActiveSignature(null)
    setSignatureData('')
  }

  const updateSignatureData = (data: string) => {
    setSignatureData(data)
  }

  const showClientNotesDialog = (clientNotes: string) => {
    setSelectedClientNotes(clientNotes)
    setShowClientNotesModal(true)
  }

  // Check if any work order has client notes
  const clientNotes = localWorkOrders.find(wo => wo.clientNotes && wo.clientNotes.trim() !== '')?.clientNotes
  const hasClientNotes = !!clientNotes

  // Table view for tablet
  const renderTableView = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              Property
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              Pick Up Key
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
              Available
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              Drop Off Key
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
              Available
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {localWorkOrders.filter(workOrder => workOrder.keyPickup).map((workOrder) => (
            <tr key={workOrder.workOrderID}>
              <td className="px-3 py-4 text-sm text-gray-900 break-words">
                <div>
                  <div>{workOrder.propertyAddress}</div>
                  <div className="mt-1">
                    <div className="inline-flex items-center gap-1">
                      <KeyIcon className="h-3 w-3 text-gray-400" />
                      <Input
                        value={workOrder.puKeyNo || ''}
                        onChange={(e) => handleKeyUpdate(workOrder, 'puKeyNo', e.target.value)}
                        placeholder="Key #"
                        className="h-7 text-xs w-24"
                      />
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="space-y-2">
                  <Textarea
                    value={workOrder.puComment || ''}
                    onChange={(e) => handleKeyUpdate(workOrder, 'puComment', e.target.value)}
                    placeholder="Comment"
                    rows={1}
                    className="text-xs"
                  />
                </div>
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-center">
                <Checkbox
                  checked={workOrder.puAvailable || false}
                  onCheckedChange={(checked) => handleKeyUpdate(workOrder, 'puAvailable', checked as boolean)}
                />
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                <div className="space-y-2">
                  <Textarea
                    value={workOrder.doComment || ''}
                    onChange={(e) => handleKeyUpdate(workOrder, 'doComment', e.target.value)}
                    placeholder="Comment"
                    rows={1}
                    className="text-xs"
                  />
                </div>
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-center">
                <Checkbox
                  checked={workOrder.doAvailable || false}
                  onCheckedChange={(checked) => handleKeyUpdate(workOrder, 'doAvailable', checked as boolean)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  // Card view for mobile
  const renderCardView = () => (
    <div className="space-y-6">
      {localWorkOrders.filter(workOrder => workOrder.keyPickup).map((workOrder) => (
        <div key={workOrder.workOrderID} className="pl-4 space-y-4">
          <div className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mt-1">
                  <MapPinIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{workOrder.propertyAddress}</span>
                </div>
                {workOrder.puKeyNo && (
                  <div className="flex items-center gap-2 mt-1 ml-6">
                    <KeyIcon className="h-3 w-3 text-gray-400" />
                    <span className="text-xs text-gray-500">Key #: {workOrder.puKeyNo}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
              {/* Key Information */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Pick Up Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-green-700 flex items-center gap-2">
                    <KeyIcon className="h-4 w-4" />
                    Pick Up
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor={`pu-comment-${workOrder.workOrderID}`} className="text-sm">Comment</Label>
                      <Textarea
                        id={`pu-comment-${workOrder.workOrderID}`}
                        value={workOrder.puComment || ''}
                        onChange={(e) => handleKeyUpdate(workOrder, 'puComment', e.target.value)}
                        placeholder="Add comment..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`pu-available-${workOrder.workOrderID}`}
                        checked={workOrder.puAvailable || false}
                        onCheckedChange={(checked) => handleKeyUpdate(workOrder, 'puAvailable', checked as boolean)}
                      />
                      <Label htmlFor={`pu-available-${workOrder.workOrderID}`} className="text-sm">
                        Key Available
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Drop Off Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                    <KeyIcon className="h-4 w-4" />
                    Drop Off
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor={`do-comment-${workOrder.workOrderID}`} className="text-sm">Comment</Label>
                      <Textarea
                        id={`do-comment-${workOrder.workOrderID}`}
                        value={workOrder.doComment || ''}
                        onChange={(e) => handleKeyUpdate(workOrder, 'doComment', e.target.value)}
                        placeholder="Add comment..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`do-available-${workOrder.workOrderID}`}
                        checked={workOrder.doAvailable || false}
                        onCheckedChange={(checked) => handleKeyUpdate(workOrder, 'doAvailable', checked as boolean)}
                      />
                      <Label htmlFor={`do-available-${workOrder.workOrderID}`} className="text-sm">
                        Key Available
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      ))}
    </div>
  )

  const hasKeyPickup = localWorkOrders.some(wo => wo.keyPickup === true)
  if (!hasKeyPickup) {
    return null;
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserIcon className="h-5 w-5 text-gray-400" />
            <CardTitle className="text-lg">{client.clientName}</CardTitle>
            {hasClientNotes && (
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-auto"
                onClick={() => showClientNotesDialog(clientNotes || '')}
              >
                <StarIconSolid className="h-4 w-4 text-yellow-500" />
              </Button>
            )}
          </div>
          <Button
            variant={isExpanded ? "secondary" : "default"}
            size="sm"
            onClick={onToggle}
            className="flex items-center gap-2"
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                Close Keylist
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                View Keylist
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500">Loading work orders...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Render table view at all screen sizes */}
              {renderTableView()}

              {/* Client-level Signature Section */}
              {hasKeyPickup && (
                <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <PencilIcon className="h-4 w-4" />
                  Signatures ({localWorkOrders.filter(wo => wo.keyPickup).length} work orders)
                </h4>
                
                {/* Display existing signatures */}
                {localWorkOrders.length > 0 && (localWorkOrders[0].puSignature || localWorkOrders[0].doSignature) && (
                  <div className="mb-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Pick Up Signature Display */}
                      {localWorkOrders[0].puSignature && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-green-800 mb-2">Pick Up Signature</h5>
                          <div className="bg-white rounded border p-2">
                            <img 
                              src={apiEndpoints.files.serve(localWorkOrders[0].puSignature)} 
                              alt="Pick Up Signature"
                              className="max-w-full h-auto max-h-32 mx-auto"
                            />
                          </div>
                          {localWorkOrders[0].puDate && (
                            <p className="text-xs text-green-600 mt-2">
                              Signed: {new Date(localWorkOrders[0].puDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Drop Off Signature Display */}
                      {localWorkOrders[0].doSignature && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-blue-800 mb-2">Drop Off Signature</h5>
                          <div className="bg-white rounded border p-2">
                            <img 
                              src={apiEndpoints.files.serve(localWorkOrders[0].doSignature)} 
                              alt="Drop Off Signature"
                              className="max-w-full h-auto max-h-32 mx-auto"
                            />
                          </div>
                          {localWorkOrders[0].doDate && (
                            <p className="text-xs text-blue-600 mt-2">
                              Signed: {new Date(localWorkOrders[0].doDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {activeSignature ? (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <div className="pb-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-base font-semibold">
                          {activeSignature === 'PU' ? 'Pick Up' : 'Drop Off'} Signature
                        </h5>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelSignature}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border">
                        <SignaturePad
                          onSignatureChange={updateSignatureData}
                          width={400}
                          height={150}
                          initialSignature={runsheetService.getSignatureFromStorage()}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={cancelSignature}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={saveSignature}
                          disabled={signatureMutation.isPending}
                          className="flex-1"
                        >
                          {signatureMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleSignature('PU')}
                      className="flex items-center gap-2 border-green-200 text-green-700 hover:bg-green-50"
                    >
                      <PencilIcon className="h-4 w-4" />
                      {localWorkOrders.length > 0 && localWorkOrders[0].puSignature ? 'Update Pick Up' : 'Pick Up Signature'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSignature('DO')}
                      className="flex items-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      <PencilIcon className="h-4 w-4" />
                      {localWorkOrders.length > 0 && localWorkOrders[0].doSignature ? 'Update Drop Off' : 'Drop Off Signature'}
                    </Button>
                  </div>
                )}
              </div>
              )}
            </div>
          )}
        </CardContent>
      )}

      {/* Client Notes Modal */}
      <Dialog open={showClientNotesModal} onOpenChange={setShowClientNotesModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Client Notes</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedClientNotes}</p>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

const Runsheet: React.FC = () => {
  const { user, addNotification, selectedDate, setSelectedDate } = useAppStore()
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<number>(
    user?.technicianId || parseInt(user?.id || '0')
  )
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set())

  // Get technicians list
  const { data: technicians = [] } = useQuery({
    queryKey: ['technicians'],
    queryFn: () => runsheetService.getTechnicians(),
    staleTime: 0, // Data is immediately stale for aggressive refresh
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  })

  // Get runsheet data
  const { data: clients = [], isLoading, error } = useQuery({
    queryKey: ['runsheet', selectedTechnicianId, selectedDate],
    queryFn: () => runsheetService.getRunsheet(selectedTechnicianId, selectedDate),
    enabled: !!selectedTechnicianId,
    staleTime: 0, // Data is immediately stale for aggressive refresh
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  useEffect(() => {
    if (error) {
      addNotification({
        type: 'error',
        message: `Failed to load runsheet: ${error.message}`,
        timestamp: Date.now()
      })
    }
  }, [error, addNotification])

  const toggleClient = (clientId: number) => {
    const newExpandedClients = new Set(expandedClients)
    if (newExpandedClients.has(clientId)) {
      newExpandedClients.delete(clientId)
    } else {
      newExpandedClients.add(clientId)
    }
    setExpandedClients(newExpandedClients)
  }

  // Prepare technician options for dropdown
  const technicianOptions: SelectOption[] = technicians.map(tech => ({
    value: tech.id,
    label: tech.name
  }))

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Technician Keylist
          </h1>
          <p className="text-gray-600 mt-1">
            Manage keys and signatures for scheduled work orders
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium whitespace-nowrap">
              <UsersIcon className="h-4 w-4 inline mr-1" />
              Tech:
            </Label>
            <Select
              options={technicianOptions}
              value={selectedTechnicianId}
              onChange={(value) => setSelectedTechnicianId(value as number)}
              placeholder="Select technician"
            />
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="date-selector" className="text-sm font-medium">Date:</Label>
            <Input
              id="date-selector"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-auto"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading runsheet...</p>
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-4xl">📝</div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-900">No Jobs Found</h4>
              <p className="text-gray-500 mt-1">
                No scheduled work orders found for the selected technician and date.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {clients.map((client) => (
            <ClientKeylist
              key={client.clientID}
              client={client}
              date={selectedDate}
              isExpanded={expandedClients.has(client.clientID)}
              onToggle={() => toggleClient(client.clientID)}
              selectedTechnicianId={selectedTechnicianId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Runsheet 